/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Button, DateInput, Form, Grid, Input } from '@mairistem/composants';

import type { Pacs, Registre } from '@/types';
import StatutPacs from '@/enums/StatutPacs';
import { MOTIFS_IRRECEVABILITE } from '@/constants';
import { validationInformationsPacs } from '@/utils/validations';
import { ModalRegistreTreeView } from '@/components/Registre';

import PanelModalEdition from '../PanelModalEdition';

const OPTIONS_STATUT_PACS = [
  { key: 'en_cours', text: StatutPacs.EN_COURS, value: StatutPacs.EN_COURS },
  {
    key: 'irrecevable',
    text: StatutPacs.IRRECEVABLE,
    value: StatutPacs.IRRECEVABLE,
  },
];

const ORDRE_DISSOLUTION = 999;

interface InformationsPacsModalProps {
  numero: string;
  dateEnregistrement: string;
  statut?: StatutPacs | null;
  enregistre?: boolean;
  dateIrrecevabilite: string;
  identifiantIrrecevabilite: number;
  anneeRegistre: number;
  libelleRegistre: string;
  ordre?: number;
  dateInitialeInsee: string;
  dateDissolutionInsee: string;
  registre?: Partial<Registre> | null;
  updated: boolean;
  onSave: () => Promise<void>;
  onChange: (data: Partial<Pacs>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsPacsModal = (props: InformationsPacsModalProps) => {
  const {
    numero,
    dateEnregistrement,
    statut,
    enregistre = false,
    dateIrrecevabilite,
    identifiantIrrecevabilite,
    anneeRegistre,
    libelleRegistre,
    ordre,
    dateInitialeInsee,
    dateDissolutionInsee,
    registre: registreProp,
    updated,
    onSave,
    onChange,
    onClose,
    onCancel,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [openRegistreModal, setOpenRegistreModal] =
    React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});
  const [selectedRegistre, setSelectedRegistre] =
    React.useState<Partial<Registre> | null>(null);

  const isDissous = statut === StatutPacs.DISSOUS;
  const isProjet = statut === StatutPacs.PROJET;
  const isIrrecevable = statut === StatutPacs.IRRECEVABLE;
  const isConventionDissolution = ordre === ORDRE_DISSOLUTION;

  // Le statut d'un PACS dissous ou d'un projet de PACS n'est pas modifiable
  // depuis cette modale : le champ est alors désactivé.
  const isStatutLectureSeule = isDissous || isProjet;

  // Convention modificative (cd_ordre >= 1) ne peut être déclaré irrecevable
  const hasModificative =
    ordre != null && ordre >= 1 && ordre < ORDRE_DISSOLUTION;

  // Un PACS dont l'enregistrement de la déclaration est confirmé, ou ayant une
  // convention modificative, ne peut pas être déclaré irrecevable (sauf s'il
  // l'est déjà : on garde son statut affiché)
  const irrecevabiliteInterdite =
    (enregistre || hasModificative) && !isIrrecevable;

  // Un statut en lecture seule n'est jamais un choix possible : on ne
  // l'ajoute que pour afficher le statut actuel du PACS.
  const statutOptions = React.useMemo(() => {
    const options = irrecevabiliteInterdite
      ? OPTIONS_STATUT_PACS.filter(
          (option) => option.value !== StatutPacs.IRRECEVABLE,
        )
      : OPTIONS_STATUT_PACS;

    return isStatutLectureSeule && statut
      ? [...options, { key: statut, text: statut, value: statut }]
      : options;
  }, [irrecevabiliteInterdite, isStatutLectureSeule, statut]);

  const activeRegistre = selectedRegistre ?? registreProp ?? null;
  const canEditNumero =
    !isDissous && (activeRegistre as any)?.numerotationManuelle === 1;

  const registreLabel = React.useMemo(() => {
    if (selectedRegistre) {
      return `Année ${(selectedRegistre as any)?.annee?.annee} - ${selectedRegistre.libelle}`;
    }
    if (anneeRegistre && libelleRegistre) {
      return `Année ${anneeRegistre} - ${libelleRegistre}`;
    }
    return '';
  }, [anneeRegistre, libelleRegistre, selectedRegistre]);

  const handleSelectRegistre = React.useCallback(
    (registre: Registre) => {
      setSelectedRegistre(registre);
      setOpenRegistreModal(false);
      onChange({
        anneeRegistre: (registre as any)?.annee?.annee,
        libelleRegistre: registre.libelle,
      });
    },
    [onChange],
  );

  const handleChangeDateEnregistrement = React.useCallback(
    (date: Date | null) => {
      onChange({
        derniereConvention: {
          dateEnregistrement: date ? dayjs(date).format('YYYY-MM-DD') : '',
        } as any,
      });
    },
    [onChange],
  );

  const handleChangeNumero = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { value: string }) => {
      onChange({ numero: data.value });
    },
    [onChange],
  );

  const handleChangeStatut = React.useCallback(
    (_e: React.SyntheticEvent, data: { value: string }) => {
      const newStatut = data.value as StatutPacs;
      onChange({
        derniereConvention: {
          statut: newStatut,
          ...(newStatut !== StatutPacs.IRRECEVABLE && {
            dateIrrecevabilite: null,
            identifiantIrrecevabilite: 0,
            irrecevabilite: null,
          }),
        } as any,
      });
    },
    [onChange],
  );

  const handleChangeDateIrrecevabilite = React.useCallback(
    (date: Date | null) => {
      onChange({
        derniereConvention: {
          dateIrrecevabilite: date ? dayjs(date).format('YYYY-MM-DD') : '',
        } as any,
      });
    },
    [onChange],
  );

  const handleChangeMotifIrrecevabilite = React.useCallback(
    (_e: React.SyntheticEvent, data: { value: number }) => {
      onChange({
        derniereConvention: {
          identifiantIrrecevabilite: data.value,
        } as any,
      });
    },
    [onChange],
  );

  const handleChangeDateInitialeInsee = React.useCallback(
    (date: Date | null) => {
      onChange({
        dateInitialeInsee: date ? dayjs(date).format('YYYY-MM-DD') : null,
      } as any);
    },
    [onChange],
  );

  const handleChangeDateDissolutionInsee = React.useCallback(
    (date: Date | null) => {
      onChange({
        dateDissolutionInsee: date ? dayjs(date).format('YYYY-MM-DD') : null,
      } as any);
    },
    [onChange],
  );

  const handleSave = React.useCallback(() => {
    const validationData = {
      conventionDeclaration: {
        dateEnregistrement,
        dateIrrecevabilite,
        motifIrrecevabilite: identifiantIrrecevabilite
          ? String(identifiantIrrecevabilite)
          : '',
      },
      registre: activeRegistre ?? { identifiant: anneeRegistre ? 1 : null },
      numero,
    };

    const e = validationInformationsPacs(
      validationData,
      isIrrecevable,
    ) as Record<string, string>;

    // Validate numero against numLastPacs if numerotationManuelle
    if (canEditNumero && numero && activeRegistre) {
      const numLastPacs = (activeRegistre as any)?.numLastPacs;

      if (
        numLastPacs != null &&
        parseInt(numero, 10) > parseInt(String(numLastPacs), 10)
      ) {
        e.numero = `Le numéro ne peut pas dépasser ${numLastPacs}`;
      }
    }

    setErrors(e);

    if (_.isEmpty(e)) {
      setLoading(true);

      onSave()
        .then(() => {
          onClose();
        })
        .catch(() => {
          setErrors({ api: true });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    activeRegistre,
    anneeRegistre,
    canEditNumero,
    dateEnregistrement,
    dateIrrecevabilite,
    identifiantIrrecevabilite,
    isIrrecevable,
    numero,
    onClose,
    onSave,
  ]);

  return (
    <>
      <PanelModalEdition
        data-testid="informations-pacs-modal"
        errors={errors}
        loading={loading}
        header="INFORMATIONS DU PACS"
        updated={updated}
        onSave={handleSave}
        onClose={onCancel}
      >
        <Grid as={Form}>
          <Grid.Row>
            <Grid.Column width={6}>
              <Form.Field error={!_.isEmpty(errors?.registre)}>
                <Input
                  fluid
                  input={{ 'data-testid': 'informations-pacs-registre' }}
                  label="Enregistré sur le registre"
                  value={registreLabel}
                  readOnly
                  action={
                    <Button
                      type="button"
                      basic
                      icon="search"
                      disabled={isDissous}
                      data-testid="informations-pacs-registre-bouton-recherche"
                      onClick={() => setOpenRegistreModal(true)}
                    />
                  }
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column width={4}>
              <Form.Field error={!_.isEmpty(errors?.dateEnregistrement)}>
                <label>Date d&apos;enregistrement</label>
                <DateInput
                  value={
                    dateEnregistrement
                      ? new Date(dateEnregistrement)
                      : undefined
                  }
                  data-testid="informations-pacs-date-enregistrement"
                  onChange={handleChangeDateEnregistrement}
                  error={!_.isEmpty(errors?.dateEnregistrement)}
                  disabled={isDissous}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column width={3}>
              <Form.Input
                fluid
                input={{ 'data-testid': 'informations-pacs-numero' }}
                label="Numéro"
                value={numero ?? ''}
                onChange={handleChangeNumero}
                error={!_.isEmpty(errors?.numero)}
                disabled={!canEditNumero}
                title={
                  !canEditNumero && !isDissous
                    ? 'La numérotation automatique est activée sur ce registre'
                    : undefined
                }
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Form.Dropdown
                selection
                data-testid="informations-pacs-statut"
                label="Statut"
                options={statutOptions}
                value={statut}
                onChange={handleChangeStatut}
                disabled={isStatutLectureSeule}
              />
            </Grid.Column>
          </Grid.Row>
          {isIrrecevable && (
            <Grid.Row>
              <Grid.Column width={4}>
                <Form.Field error={!_.isEmpty(errors?.dateIrrecevabilite)}>
                  <label>Date d&apos;irrecevabilité</label>
                  <DateInput
                    value={
                      dateIrrecevabilite
                        ? new Date(dateIrrecevabilite)
                        : undefined
                    }
                    data-testid="informations-pacs-date-irrecevabilite"
                    onChange={handleChangeDateIrrecevabilite}
                    error={!_.isEmpty(errors?.dateIrrecevabilite)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={8}>
                <Form.Dropdown
                  selection
                  fluid
                  label="Motif d&apos;irrecevabilité"
                  data-testid="informations-pacs-motif-irrecevabilite"
                  options={MOTIFS_IRRECEVABILITE}
                  value={identifiantIrrecevabilite}
                  onChange={handleChangeMotifIrrecevabilite}
                  error={!_.isEmpty(errors?.motifIrrecevabilite)}
                />
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row>
            <Grid.Column width={5}>
              <Form.Field>
                <label>Déclaration transférée à l&apos;INSEE le</label>
                <DateInput
                  value={
                    dateInitialeInsee ? new Date(dateInitialeInsee) : undefined
                  }
                  data-testid="informations-pacs-date-insee-initiale"
                  onChange={handleChangeDateInitialeInsee}
                />
              </Form.Field>
            </Grid.Column>
            {isConventionDissolution && (
              <Grid.Column width={5}>
                <Form.Field>
                  <label>Dissolution transférée à l&apos;INSEE le</label>
                  <DateInput
                    value={
                      dateDissolutionInsee
                        ? new Date(dateDissolutionInsee)
                        : undefined
                    }
                    data-testid="informations-pacs-date-insee-dissolution"
                    onChange={handleChangeDateDissolutionInsee}
                  />
                </Form.Field>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </PanelModalEdition>
      <ModalRegistreTreeView
        open={openRegistreModal}
        isProjet={isProjet}
        onClose={() => setOpenRegistreModal(false)}
        onSelect={handleSelectRegistre}
      />
    </>
  );
};

export default InformationsPacsModal;
